import { useEffect, useRef, useState } from "react";
import Main from "../../components/custom/Main";
import PageContainer from "../../components/custom/PageContainer";
import { LogIn, Coffee, LogOut, Clock } from "lucide-react";
import "../../styles/Attendance.css";
import React from "react";
import { EmployeeAttendance } from "../../types/Attendance";
import { useDispatch, useSelector } from "react-redux";
import {
  addEmployeeAttendanceAction,
  editEmployeeAttendanceAction,
  fetchEmployeeAttendanceAction,
} from "../../redux/actions/employeeAttendanceAction";
import {
  endBreakAction,
  fetchTotalBreakByDateAction,
  fetchTotalBreakCheckedOutAction,
  startBreakAction,
} from "../../redux/actions/employeeBreakAction";
import { EmployeeBreak } from "../../types/EmployeeBreak";
import { StoreState } from "../../redux/reducers";
import CollapseCard from "../../components/custom/CollapseCard";
import AttendanceList from "../../components/AttendanceList";
import { AppDispatch } from "../../redux/store";
import { useToast } from "../../components/toast/ToastProvider";

const Attendance = () => {
  const attendanceList = useSelector(
    (state: StoreState) => state.employeeAttendance,
  );
  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();
  const [currentTime, setCurrentTime] = useState(getCurrentTime());
  const [checkInTime, setCheckInTime] = useState<Date | null>(null);
  const [checkOutTime, setCheckOutTime] = useState<Date | null>(null);
  // Break duration

  const [currentBreakDuration, setCurrentBreakDuration] = useState<number>(0);
  const [lastBreakEndTime, setLastBreakEndTime] = useState<Date | null>(null);
  const [breakStartTime, setBreakStartTime] = useState<Date | null>(null);
  const [totalBreakMs, setTotalBreakMs] = useState<number>(0);
  const [isBreakActive, setIsBreakActive] = useState(false);
  const [workingHours, setWorkingHours] = useState<string>("0h 0m 0s");

  const users = useSelector((state: StoreState) => state.authUser.employeeId);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(getCurrentTime());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  function getCurrentTime() {
    return new Date().toLocaleTimeString("en-US", { hour12: false });
  }

  const handleCheckIn = async () => {
    if (!checkInTime) {
      const now = new Date();
      // const today = now.toISOString().split("T")[0];

      // const alreadyCheckedInToday = attendanceList.some((a) =>
      //   a.employeeId === users &&
      //   a.date === now.toISOString().split("T")[0] &&
      //   (a.status === "checkedIn" || a.status === "checkedOut")
      // );

      // if (alreadyCheckedInToday) {
      //   console.warn("Already checked in today.");
      //   alert("You have already checked in today.");
      //   return;
      // }
      setCheckInTime(now);
      localStorage.setItem("checkInTime", now.toISOString());
      localStorage.setItem("checkInDate", now.toISOString().split("T")[0]);
      localStorage.setItem("checkInStatus", "checkedIn");

      const attendanceData: EmployeeAttendance = {
        employeeId: users,
        date: now.toISOString().split("T")[0],
        action: "checkin",
        checkin: now.toLocaleTimeString(),
        checkout: "",
        comment: "",
        breakHrs: 0,
        status: "checkedIn",
      };
      try {
        await dispatch(addEmployeeAttendanceAction(attendanceData));
        await dispatch(fetchEmployeeAttendanceAction());
      } catch (error) {
        console.error("Failed to check in:", error);
      }
    }
  };

  useEffect(() => {
    const storedCheckInTime = localStorage.getItem("checkInTime");
    const storedDate = localStorage.getItem("checkInDate");
    const storedStatus = localStorage.getItem("checkInStatus");
    const today = new Date().toISOString().split("T")[0];

    if (
      storedCheckInTime &&
      storedDate === today &&
      storedStatus === "checkedIn"
    ) {
      setCheckInTime(new Date(storedCheckInTime));
    } else {
      localStorage.removeItem("checkInTime");
      localStorage.removeItem("checkInDate");
      localStorage.removeItem("checkInStatus");
    }
  }, []);

  const handleCheckOut = async () => {
    if (isAlreadyCheckedOut()) return;

    const now = new Date();
    const isoDate = now.toISOString().split("T")[0];

    const todayCheckin = attendanceList.find((a) => {
      const attDate = a.date?.split("T")[0] || a.date;
      return attDate === isoDate || a.action === "checkin";
    });

    if (!todayCheckin) {
      console.error("Invalid or missing check-in ID", todayCheckin);
      return;
    }

    try {
      if (isBreakActive && breakStartTime) {
        toast("please end your break before checking out");
        return;
      }
      const breakResponse = await dispatch(
        fetchTotalBreakCheckedOutAction(users, isoDate),
      );
      const responseData = breakResponse
        ? breakResponse
        : breakResponse?.payload;
      const totalBreakHours = responseData?.[0]?.total_break_hours;
      const breakHrs =
        totalBreakHours != null ? +totalBreakHours.toFixed(2) : 0;
      console.log("Final breakHrs:", breakHrs);
      const updateData: EmployeeAttendance = {
        id: todayCheckin.id,
        employeeId: users,
        date: isoDate,
        action: "checkout",
        checkin: todayCheckin.checkin || new Date().toLocaleTimeString(),
        checkout: now.toLocaleTimeString(),
        comment: "",
        breakHrs,
        status: "checkedOut",
      };

      if (updateData.id !== undefined) {
        await dispatch(editEmployeeAttendanceAction(updateData.id, updateData));
      } else {
        console.error(
          "updateData.id is undefined, cannot edit attendance record.",
        );
      }
      await dispatch(fetchEmployeeAttendanceAction());
      setCheckOutTime(now);
      calculateWorkingHours(now);
      localStorage.setItem("checkOutStatus", "checkedOut");
      localStorage.setItem("checkOutDate", isoDate);
      localStorage.setItem("checkOutTime", now.toISOString());
    } catch (error) {
      console.error("Failed to checkout:", error);
    }
  };

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    const savedCheckOutDate = localStorage.getItem("checkOutDate");
    const savedStatus = localStorage.getItem("checkOutStatus");
    const savedCheckOutTime = localStorage.getItem("checkOutTime");

    if (
      savedCheckOutDate === today &&
      savedStatus === "checkedOut" &&
      savedCheckOutTime
    ) {
      setCheckOutTime(new Date(savedCheckOutTime));
    } else {
      localStorage.removeItem("checkOutStatus");
      localStorage.removeItem("checkOutDate");
      localStorage.removeItem("checkOutTime");
    }
  }, []);

  const isAlreadyCheckedIn = () => {
    const todayDate = new Date().toLocaleString();
    return (
      checkInTime ||
      attendanceList.some((a) => a.date === todayDate && a.action === "checkin")
    );
  };

  const isAlreadyCheckedOut = () => {
    const isoDate = new Date().toISOString().split("T")[0];
    const todayCheckout = attendanceList.find(
      (a) => a.date === isoDate && a.action === "checkout",
    );
    return !!checkOutTime || !!todayCheckout;
  };

  const handleBreak = async () => {
    if (checkOutTime) return;

    const now = new Date();
    const dateStr = now.toISOString().split("T")[0];

    if (isBreakActive) {
      if (breakStartTime) {
        const breakEndData: EmployeeBreak = {
          employeeId: users,
          action: "end",
          date: dateStr,
          start: formatToTimestamp(breakStartTime),
          end: formatToTimestamp(now),
        };
        try {
          await dispatch(endBreakAction(breakEndData));
          await dispatch(fetchEmployeeAttendanceAction());
          const res = await dispatch(
            fetchTotalBreakByDateAction(users, dateStr),
          );
          const responseData = res ? res : res?.payload;
          const total = responseData[0]?.total_break_hours;
          console.log("Total Break Minutes:", total);
          const totalMs = total * 60 * 1000;

          if (breakTimerRef.current) clearInterval(breakTimerRef.current);
          setTotalBreakMs(totalMs);
          setCurrentBreakDuration(totalMs);
          setLastBreakEndTime(now);

          localStorage.setItem(
            "breakInfo",
            JSON.stringify({
              date: dateStr,
              isBreakActive: false,
              start: breakStartTime?.toISOString(),
              end: formatToTimestamp(now),
              totalBreakMs: totalMs,
            }),
          );
          setIsBreakActive(false);
          setBreakStartTime(null);
        } catch (error) {
          console.error("Failed to end break", error);
        }
      }
    } else {
      const breakStartData: EmployeeBreak = {
        employeeId: users,
        action: "start",
        date: dateStr,
        start: formatToTimestamp(now),
        end: "",
      };

      try {
        await dispatch(startBreakAction(breakStartData));
        await dispatch(fetchEmployeeAttendanceAction());
        setIsBreakActive(true);
        setBreakStartTime(now);
        setLastBreakEndTime(null);
        localStorage.setItem(
          "breakInfo",
          JSON.stringify({
            date: dateStr,
            isBreakActive: true,
            start: now.toISOString(),
            end: "",
            totalBreakMs: totalBreakMs,
          }),
        );
        startBreakTimer(now, totalBreakMs);
      } catch (error) {
        console.error("Failed to start break", error);
      }
    }
  };
  const breakTimerRef = useRef<NodeJS.Timeout | null>(null);
  const startBreakTimer = (startTime: Date, baseMs: number = 0) => {
    if (breakTimerRef.current) clearInterval(breakTimerRef.current);

    breakTimerRef.current = setInterval(() => {
      const now = new Date();
      const elapsed = now.getTime() - startTime.getTime();
      setCurrentBreakDuration(baseMs + elapsed);
    }, 1000);
  };

  useEffect(() => {
    const saved = localStorage.getItem("breakInfo");
    if (saved) {
      const {
        date,
        isBreakActive,
        start,
        end,
        totalBreakMs: savedTotalMs,
      } = JSON.parse(saved);
      const today = new Date().toISOString().split("T")[0];

      if (date === today) {
        if (isBreakActive && start) {
          const startTime = new Date(start);
          const now = new Date();
          const elapsed = now.getTime() - startTime.getTime();
          const total = (savedTotalMs || 0) + elapsed;

          setIsBreakActive(true);
          setBreakStartTime(startTime);
          setTotalBreakMs(savedTotalMs || 0);
          setCurrentBreakDuration(total);
          startBreakTimer(startTime, savedTotalMs || 0);
        } else if (!isBreakActive && start && end) {
          const endTime = new Date(end);
          const startTime = new Date(start);
          const totalMs =
            savedTotalMs ?? endTime.getTime() - startTime.getTime();

          setIsBreakActive(false);
          setBreakStartTime(null);
          setTotalBreakMs(totalMs);
          setCurrentBreakDuration(totalMs);
          setLastBreakEndTime(endTime);
        }
      } else {
        localStorage.removeItem("breakInfo");
      }
    }

    return () => {
      if (breakTimerRef.current) clearInterval(breakTimerRef.current);
    };
  }, []);

  const calculateWorkingHours = (customOutTime?: Date) => {
    if (checkInTime) {
      const outTime = customOutTime || new Date();

      let effectiveMs = outTime.getTime() - checkInTime.getTime();
      console.log("Effective MS:", effectiveMs, "Break MS Used:", totalBreakMs);
      effectiveMs -= totalBreakMs;
      const formatted = formatMsToHMS(effectiveMs > 0 ? effectiveMs : 0);
      setWorkingHours(formatted);

      const todayStr = new Date().toISOString().split("T")[0];
      localStorage.setItem("workingHours", formatted);
      localStorage.setItem("workingHoursDate", todayStr);
    }
  };

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    const storedDate = localStorage.getItem("workingHoursDate");
    const storedHrs = localStorage.getItem("workingHours");

    if (storedDate === today && storedHrs) {
      setWorkingHours(storedHrs);
    } else {
      localStorage.removeItem("workingHours");
      localStorage.removeItem("workingHoursDate");
      setWorkingHours("00:00:00");
    }
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (checkInTime && !checkOutTime && !isBreakActive) {
      interval = setInterval(() => {
        calculateWorkingHours();
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [checkInTime, checkOutTime, isBreakActive, totalBreakMs]);

  const formatToTimestamp = (date: Date): string => {
    const pad = (n: number) => String(n).padStart(2, "0");
    return (
      `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
      `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    );
  };

  const getCurrentFormattedDate = () => {
    const today = new Date();
    const day = today.getDate();
    const daySuffix = (d: number) => {
      if (d > 3 && d < 21) return "th";
      switch (d % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    };
    const formattedDate = `${day}${daySuffix(day)} ${today.toLocaleString("default", { month: "long" })} ${today.getFullYear()}`;
    return formattedDate;
  };

  const formatMsToHMS = (ms: number) => {
    const totalSecs = Math.floor(ms / 1000);
    const hrs = Math.floor(totalSecs / 3600);
    const mins = Math.floor((totalSecs % 3600) / 60);
    const secs = totalSecs % 60;
    return `${hrs}h ${mins}m ${secs}s`;
  };

  return (
    <Main>
      <PageContainer title="Attendance">
        <div className="max-w-6xl mx-auto mt-0 mb-3 bg-white shadow-md rounded-3 px-5 pt-4 pb-3">
          <div className="d-flex justify-content-between mb-2">
            <h2 className="text-lg font-semibold mb-6">
              {getCurrentFormattedDate()}
            </h2>
            <h2 className="text-lg font-semibold mb-6">{currentTime}</h2>
          </div>
          {/* <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "1rem" }}>
            <div
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                backgroundColor: "#f1f1f1",
                boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
                fontSize: "14px",
                fontWeight: 500,
                display: "inline-block",
                minWidth: "100px",
                textAlign: "center",
              }}
            >
              <span
              >

                {attendanceList.length > 0 ? (
                  attendanceList.map((a) => (
                    <div key={a.id || a.date}>
                      <span>{a.status || " Status"}</span>
                    </div>
                  ))
                ) : (
                  <div>Status</div>
                )}
              </span>
            </div>
          </div> */}
          <div className="attendance-card-row">
            <div
              className="attendance-card"
              style={{
                backgroundColor: "#d4edda",
                color: "#155724",
                cursor: isAlreadyCheckedIn() ? "not-allowed" : "pointer",
                opacity: isAlreadyCheckedIn() ? 0.6 : 1,
              }}
              onClick={!isAlreadyCheckedIn() ? handleCheckIn : undefined}
            >
              <LogIn className="mb-2" />
              <div className="fw-bold">
                {checkInTime
                  ? checkInTime.toLocaleTimeString("en-US", { hour12: false })
                  : (() => {
                      const todayDate = new Date().toLocaleString();
                      const todayCheckin = attendanceList.find(
                        (a) => a.date === todayDate && a.action === "checkin",
                      );
                      return todayCheckin
                        ? new Date(todayCheckin.date).toLocaleTimeString(
                            "en-US",
                            { hour12: false },
                          )
                        : "Click to Check In";
                    })()}
              </div>
              <small>Check in</small>
            </div>

            <div
              className="attendance-card"
              style={{
                backgroundColor: isBreakActive ? "#f1b0b7" : "#f8d7da",
                color: checkOutTime ? "gray" : "#721c24",
                cursor: checkOutTime ? "not-allowed" : "pointer",
                opacity: checkOutTime ? 0.6 : 1,
              }}
              onClick={handleBreak}
            >
              <Coffee className="mb-2" />
              <div className="fw-bold">
                {isBreakActive && breakStartTime
                  ? formatMsToHMS(currentBreakDuration)
                  : formatMsToHMS(totalBreakMs)}
              </div>

              <small>
                {isBreakActive ? "Click to Stop Break" : "Click to Start Break"}
              </small>
            </div>

            <div
              className="attendance-card"
              style={{
                backgroundColor: "#fff3cd",
                color: "#856404",
                cursor: isAlreadyCheckedOut() ? "not-allowed" : "pointer",
                opacity: isAlreadyCheckedOut() ? 0.6 : 1,
              }}
              onClick={handleCheckOut}
            >
              <LogOut className="mb-2" />
              <div className="fw-bold">
                {(() => {
                  const todayDate = new Date().toLocaleString();
                  const todayCheckout = attendanceList.find(
                    (a) => a.date === todayDate && a.action === "checkout",
                  );
                  return checkOutTime
                    ? checkOutTime.toLocaleTimeString("en-US", {
                        hour12: false,
                      })
                    : todayCheckout
                      ? new Date(todayCheckout.date).toLocaleTimeString(
                          "en-US",
                          { hour12: false },
                        )
                      : "Click to Check Out";
                })()}
              </div>
              <small>Check out</small>
            </div>

            <div
              className="attendance-card"
              style={{ backgroundColor: "#d1ecf1", color: "#0c5460" }}
            >
              <Clock className="mb-2" />
              <div className="fw-bold">{workingHours}</div>
              <small>Working hours</small>
            </div>
          </div>
        </div>
        <CollapseCard title="Attendance List">
          <AttendanceList />
        </CollapseCard>
      </PageContainer>
    </Main>
  );
};

export default Attendance;
